import { useState, useEffect } from "react";
import { formatRelative, subDays } from "date-fns";
import { useFormik } from "formik";
import {
  updateInvestmentRequest,
  clearUpdateInvestmentSuccess,
} from "../../redux/action";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { PrimaryButton } from "../Buttons";
import CurrencyDisplay from "../Currency/CurrencyDisplay";
import { Switch } from "@material-ui/core";

export const EditInvestmentForm = ({
  investment,
  handleClose,
  getUserInvestmentsRequest,
}) => {
  const dispatch = useDispatch();
  const [active, setActive] = useState(false);

  const {
    updateInvestmentSuccess,
    updateInvestmentError,
    updateInvestmentLoading,
  } = useSelector((state) => {
    const {
      success: { updateInvestment },
      errors: { updateInvestment: updateError },
    } = state.ajaxStatuses;
    const { updateInvestmentLoading } = state.loadingIndicator;
    return {
      updateInvestmentSuccess: updateInvestment,
      updateInvestmentError: updateError,
      updateInvestmentLoading,
    };
  });

  useEffect(() => {
    if (updateInvestmentError) toast.error(updateInvestmentError);
    if (updateInvestmentSuccess) {
      toast.success(updateInvestmentSuccess);
      dispatch(getUserInvestmentsRequest(investment.user));
      handleClose();
      dispatch(clearUpdateInvestmentSuccess());
    }
  }, [
    updateInvestmentError,
    updateInvestmentSuccess,
    dispatch,
    getUserInvestmentsRequest,
    investment.user,
    handleClose,
  ]);

  const formik = useFormik({
    initialValues: {
      expectedReturn: "",
      isActive: false,
    },
    onSubmit: (values) => {
      const formData = values;
      const id = investment._id;
      const userId = investment.user;
      dispatch(updateInvestmentRequest({ formData, userId, id }));
    },
  });

  useEffect(() => {
    setActive(investment.isActive);
    formik.setValues({
      expectedReturn: investment.expectedReturn || "",
      isActive: investment.isActive,
    });
  }, [investment]);

  const handleActiveChange = (event) => {
    const isChecked = event.target.checked;
    setActive(isChecked);
    formik.setFieldValue("isActive", isChecked);
  };

  return (
    <div className="space-y-6">
      {/* Investment Summary */}
      <div className="bg-primary/5 rounded-2xl p-4 border border-primary/10">
        <h2 className="text-lg font-heading font-bold text-gray-900 mb-1">
          {investment.planName}
        </h2>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-500">
          <span>
            Growth:{" "}
            <span className="font-bold text-gray-700">
              {investment.profitProduction}%
            </span>
          </span>
          <span>
            Invested:{" "}
            <span className="font-bold text-gray-700">
              <CurrencyDisplay
                usdAmount={investment.amount}
                className="inline"
              />
            </span>
          </span>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Created:{" "}
          {investment &&
            formatRelative(
              subDays(new Date(investment.createdAt), 0),
              new Date(),
            )}
        </p>
      </div>

      <form onSubmit={formik.handleSubmit} className="space-y-6">
        {/* Toggle Active Status */}
        <div className="flex items-center justify-between p-3 border border-gray-200 rounded-xl">
          <div>
            <label className="block text-sm font-bold text-gray-900">
              Investment Status
            </label>
            <p className="text-xs text-gray-500">
              Toggle to pause or activate profit accumulation
            </p>
          </div>
          <Switch
            checked={active}
            onChange={handleActiveChange}
            color="primary"
            name="isActive"
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </div>

        {/* Claimable Amount Input */}
        <div>
          <label className="block text-xs font-heading font-bold text-gray-700 uppercase tracking-wide mb-2">
            Claimable Return (K)
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-500 font-bold">K</span>
            </div>
            <input
              type="number"
              {...formik.getFieldProps("expectedReturn")}
              className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all outline-none font-bold text-gray-900"
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-2">
          <PrimaryButton
            text={
              updateInvestmentLoading
                ? "Saving Changes..."
                : "Update Investment"
            }
            action={formik.handleSubmit}
            loading={updateInvestmentLoading}
            className="w-full"
          />
        </div>
      </form>
    </div>
  );
};
